import { Button, Col, Image, Input, message, Popconfirm, Row, Switch, Upload, Typography } from "antd";
import Modal from "antd/lib/modal/Modal";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileTextOutlined,
  FileZipOutlined,
  VideoCameraOutlined,
  AudioOutlined,
  CodeOutlined,
  FileImageOutlined,
} from "@ant-design/icons";
import Urls, { MOBILE_URL,  postRequest } from "../../services/common/urls";
import lang from "../../services/lang";
import "./InstallConfigModal.css";

const { Text } = Typography;

function InstallConfigModal(props) {
  const { langIndex } = useSelector((x) => x.app);
  const {
    isShowInstallConfigModal,
    setIsShowInstallConfigModal,
    customer_id,
    machineInfo,
  } = props;

  const [installConfig, setInstallConfig] = useState({
    machine_name: "",
    serial_number: "",
    machine_ip_address: "",
    machine_port: "",
    hmi_ip_address: "",
    slytek_plc_ip_address: "",
    cycle_signal: "",
    cycle_interlock_interface: "",
    cycle_interlock_on: "",
    cycle_interlock_open: "",
    notes: "",
    images: [], // Keep 'images' for backward compatibility with backend
  });

  const [isShowUploadModal, setIsShowUploadModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const loadInstallConfig = () => {
    const url = Urls.GET_MACHINE_INSTALL_CONFIG;
    const param = {
      customer_id: customer_id,
      machine_id: machineInfo.machine_id,
      timezone: 0,
    };
    postRequest(url, param, (res) => {
      if (res.status == true) {
        setInstallConfig({
          ...res.data.machine_details,
          images: res.data.images,
        });
      }
    });
  };

  useEffect(() => {
    loadInstallConfig();
  }, []);

  const handleUpload = (file) => {
    setIsUploading(true);
    var formData = new FormData();
    formData.append("image", file); // Backend API still expects "image" field name
    formData.append("factory_id", customer_id);
    formData.append("machine_id", machineInfo.machine_id);

    postRequest(Urls.UPLOAD_MACHINE_INSTALL_CONFIG_IMAGE, formData, (response) => {
      setIsUploading(false);
      if (response === null) {
        message.error(lang(langIndex, "msg_something_wrong"));
        return;
      }
      if (response.status === true) {
        message.success(lang(langIndex, "msg_update_success"));
        setIsShowUploadModal(false);
        loadInstallConfig();
      } else {
        message.error(response.message || lang(langIndex, "msg_something_wrong"));
      }
    });
    return false; // Prevent default upload
  };

  const handleDeleteFile = (file) => {
    const fileId = file.id || file.image_id || file.image;
    
    // Send as FormData to avoid CORS preflight issues (same as upload)
    var formData = new FormData();
    formData.append("factory_id", customer_id);
    formData.append("machine_id", machineInfo.machine_id);
    formData.append("id", fileId);

    postRequest(Urls.DELETE_MACHINE_INSTALL_CONFIG_IMAGE, formData, (res) => {
      if (res === null) {
        message.error(lang(langIndex, "msg_something_wrong"));
        return;
      }
      if (res.status === true) {
        message.success(lang(langIndex, "msg_update_success"));
        loadInstallConfig();
      } else {
        message.error(res.message || lang(langIndex, "msg_something_wrong"));
      }
    });
  };

  // Get file extension from filename
  const getFileExtension = (filename) => {
    if (!filename) return "";
    const parts = filename.split(".");
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
  };

  // Check if file is an image based on extension
  const isImageFile = (filename) => {
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];
    const ext = getFileExtension(filename);
    return imageExtensions.includes(ext);
  };

  // Get appropriate icon for file type
  const getFileIcon = (filename) => {
    const ext = getFileExtension(filename);
    const iconProps = { style: { fontSize: 48, color: "#1890ff" } };
    
    switch (ext) {
      case "pdf":
        return <FilePdfOutlined {...iconProps} style={{ ...iconProps.style, color: "#ff4d4f" }} />;
      case "doc":
      case "docx":
        return <FileWordOutlined {...iconProps} style={{ ...iconProps.style, color: "#1890ff" }} />;
      case "xls":
      case "xlsx":
        return <FileExcelOutlined {...iconProps} style={{ ...iconProps.style, color: "#52c41a" }} />;
      case "ppt":
      case "pptx":
        return <FilePptOutlined {...iconProps} style={{ ...iconProps.style, color: "#faad14" }} />;
      case "txt":
      case "rtf":
        return <FileTextOutlined {...iconProps} />;
      case "zip":
      case "rar":
      case "7z":
      case "tar":
      case "gz":
        return <FileZipOutlined {...iconProps} />;
      case "mp4":
      case "avi":
      case "mov":
      case "wmv":
      case "flv":
      case "mkv":
        return <VideoCameraOutlined {...iconProps} />;
      case "mp3":
      case "wav":
      case "flac":
      case "aac":
        return <AudioOutlined {...iconProps} />;
      case "js":
      case "jsx":
      case "ts":
      case "tsx":
      case "py":
      case "java":
      case "cpp":
      case "c":
      case "cs":
      case "php":
      case "html":
      case "css":
      case "json":
      case "xml":
        return <CodeOutlined {...iconProps} />;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "bmp":
      case "webp":
      case "svg":
        return <FileImageOutlined {...iconProps} />;
      default:
        return <FileOutlined {...iconProps} />;
    }
  };

  // Get filename from path
  const getFileName = (filePath) => {
    if (!filePath) return "Unknown";
    const parts = filePath.split("/");
    return parts[parts.length - 1];
  };

  const fileUI = installConfig.images.map((file, index) => {
    const fileName = getFileName(file.image || file.filename || file.file);
    const isImage = isImageFile(fileName);
    const fileUrl = MOBILE_URL + (file.image || file.filename || file.file);

    return (
      <Col key={"file-key-" + index} className="install-config-file-wrapper" style={{ marginLeft: 8, marginBottom: 8 }}>
        <div className="install-config-file-container">
          {isImage ? (
            <Image
              height={120}
              width={120}
              src={fileUrl}
              style={{ objectFit: "cover", borderRadius: "5px" }}
              preview={{
                mask: <div style={{ display: "none" }} />, // Hide preview mask since we have delete overlay
              }}
            />
          ) : (
            <div className="install-config-file-icon-container">
              {getFileIcon(fileName)}
              <Text className="install-config-file-extension" ellipsis>
                {getFileExtension(fileName).toUpperCase()}
              </Text>
            </div>
          )}
          <div className="install-config-file-name-overlay">
            <Text className="install-config-file-name" ellipsis title={fileName}>
              {fileName}
            </Text>
          </div>
          <Popconfirm
            title={lang(langIndex, "jobentry_suretodelete")}
            onConfirm={() => handleDeleteFile(file)}
            okText={lang(langIndex, "ok") || "Yes"}
            cancelText={lang(langIndex, "cancel") || "No"}
          >
            <div className="install-config-delete-overlay">
              <DeleteOutlined className="install-config-delete-icon" />
            </div>
          </Popconfirm>
        </div>
      </Col>
    );
  });

  const setInstallConfigs = (field, value) => {
    var newSetting = { ...installConfig };
    newSetting[field] = value;
    setInstallConfig({ ...newSetting });
  };

  const handleSave = () => {
    const url = Urls.UPDATE_MACHINE_INSTALL_CONFIG;
    const param = {
      customer_id: customer_id,
      machine_id: machineInfo.machine_id,
      machine_name: installConfig.machine_name,
      serial_number: installConfig.serial_number,
      machine_ip_address: installConfig.machine_ip_address,
      machine_port: installConfig.machine_port,
      hmi_ip_address: installConfig.hmi_ip_address,
      slytek_plc_ip_address: installConfig.slytek_plc_ip_address,
      cycle_signal: installConfig.cycle_signal,
      cycle_interlock_interface: installConfig.cycle_interlock_interface,
      cycle_interlock_on: installConfig.cycle_interlock_on == 1 ? 1 : 0,
      cycle_interlock_open: installConfig.cycle_interlock_open == 1 ? 1 : 0,
      notes: installConfig.notes,
    };

    postRequest(url, param, (res) => {
      if (res === null) {
        message.error(lang(langIndex, "msg_something_wrong"));
        return;
      }
      if (res.status === true) {
        message.success(lang(langIndex, "msg_update_success"));
      } else {
        message.error(lang(langIndex, "msg_something_wrong"));
      }
    });
  };

  return (
    <div>
      <Modal
        centered
        visible={isShowInstallConfigModal}
        title={null}
        onCancel={() => setIsShowInstallConfigModal(false)}
        onOk={() => setIsShowInstallConfigModal(false)}
        closable={true}
        className="install-config-dialog-style"
        destroyOnClose={true}
        width={1000}
        cancelButtonProps={{
          style: {
            display: "none",
          },
        }}
      >
        <div>
          <div>
            <Row gutter={[12, 0]}>
              <Col span={12} className="install-config-group-container">
                <div className="install-config-group-title">
                  {lang(langIndex, "machine_name")}
                </div>
                <Input
                  className="install-config-input"
                  value={installConfig.machine_name}
                  onChange={(e) => setInstallConfigs("machine_name", e.target.value)}
                />
              </Col>
              <Col span={12} className="install-config-group-container">
                <div className="install-config-group-title">
                  {lang(langIndex, "serial_number")}
                </div>
                <Input
                  className="install-config-input"
                  value={installConfig.serial_number}
                  onChange={(e) => setInstallConfigs("serial_number", e.target.value)}
                />
              </Col>
            </Row>
          </div>
          <div style={{ marginTop: 12 }}>
            <div className="install-config-group-container">
              <div className="install-config-group-title">
                Network Settings
              </div>
              <Row gutter={[12, 8]}>
                <Col span={12}>
                  <div className="install-config-sub-group-title">
                    Machine IP Address
                  </div>
                  <Input
                    className="install-config-input"
                    value={installConfig.machine_ip_address}
                    onChange={(e) => setInstallConfigs("machine_ip_address", e.target.value)}
                    placeholder="e.g., 192.168.1.100"
                  />
                </Col>
                <Col span={12}>
                  <div className="install-config-sub-group-title">
                    Machine Port
                  </div>
                  <Input
                    className="install-config-input"
                    value={installConfig.machine_port}
                    onChange={(e) => setInstallConfigs("machine_port", e.target.value)}
                    placeholder="e.g., 502"
                  />
                </Col>
                <Col span={12}>
                  <div className="install-config-sub-group-title">
                    HMI IP Address
                  </div>
                  <Input
                    className="install-config-input"
                    value={installConfig.hmi_ip_address}
                    onChange={(e) => setInstallConfigs("hmi_ip_address", e.target.value)}
                    placeholder="e.g., 192.168.1.101"
                  />
                </Col>
                <Col span={12}>
                  <div className="install-config-sub-group-title">
                    Slytek PLC IP Address
                  </div>
                  <Input
                    className="install-config-input"
                    value={installConfig.slytek_plc_ip_address}
                    onChange={(e) => setInstallConfigs("slytek_plc_ip_address", e.target.value)}
                    placeholder="e.g., 192.168.1.102"
                  />
                </Col>
              </Row>
            </div>
          </div>
          <div className="install-config-group-container" style={{ marginTop: 12 }}>
            <div className="install-config-group-title">
              {lang(langIndex, "cycle_setting")}
            </div>
            <div className="install-config-group-details">
              <Row gutter={[12, 8]}>
                <Col span={8}>
                  <div className="install-config-sub-group-title">
                    {lang(langIndex, "cycle_signal")}
                  </div>
                  <Input
                    className="install-config-input"
                    value={installConfig.cycle_signal}
                    onChange={(e) => setInstallConfigs("cycle_signal", e.target.value)}
                  />
                </Col>
                <Col span={8}>
                  <div className="install-config-sub-group-title">
                    {lang(langIndex, "cycle_interlocked")}
                  </div>
                  <Row justify={"space-between"}>
                    <Col>
                      <div className="app-setting-radial-input-container">
                        <div>{lang(langIndex, "on_off")}</div>
                      </div>
                    </Col>
                    <Col>
                      <Switch
                        className="app-setting-radial-input"
                        checked={
                          installConfig.cycle_interlock_on == 1 ? true : false
                        }
                        onChange={(checked) =>
                          setInstallConfigs("cycle_interlock_on", checked ? 1 : 0)
                        }
                      />
                    </Col>
                  </Row>
                  <Row justify={"space-between"}>
                    <Col>
                      <div className="app-setting-radial-input-container">
                        <div>{lang(langIndex, "normally_closed_open")}</div>
                      </div>
                    </Col>
                    <Col>
                      <Switch
                        className="app-setting-radial-input"
                        checked={
                          installConfig.cycle_interlock_open == 1 ? true : false
                        }
                        onChange={(checked) =>
                          setInstallConfigs("cycle_interlock_open", checked ? 1 : 0)
                        }
                      />
                    </Col>
                  </Row>
                </Col>
                <Col span={8}>
                  <div className="install-config-sub-group-title">
                    {lang(langIndex, "cycle_interlock_interface")}
                  </div>
                  <Input.TextArea
                    className="install-config-textarea"
                    value={installConfig.cycle_interlock_interface}
                    onChange={(e) =>
                      setInstallConfigs("cycle_interlock_interface", e.target.value)
                    }
                    rows={3}
                    style={{ whiteSpace: "pre-line" }}
                  />
                </Col>
              </Row>
            </div>
          </div>

          <div className="install-config-group-container" style={{ marginTop: 12 }}>
            <div className="install-config-group-title">
              Notes
            </div>
            <Input.TextArea
              className="install-config-textarea"
              value={installConfig.notes}
              onChange={(e) => setInstallConfigs("notes", e.target.value)}
              rows={3}
              placeholder="Enter any additional notes or comments..."
              style={{ whiteSpace: "pre-line" }}
            />
          </div>

          <div style={{ marginTop: 12, textAlign: "right" }}>
            <Button type="primary" onClick={handleSave}>
              {lang(langIndex, "jobentry_save")}
            </Button>
          </div>

          <div className="install-config-group-container" style={{ marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <div className="install-config-group-title">
                {lang(langIndex, "files")}
              </div>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsShowUploadModal(true)}
              >
                {lang(langIndex, "add_new_file") || "Add New File"}
              </Button>
            </div>
            <Row>{fileUI}</Row>
          </div>

          {/* Upload File Modal */}
          <Modal
            centered
            visible={isShowUploadModal}
            title={lang(langIndex, "add_new_file") || "Add New File"}
            onCancel={() => setIsShowUploadModal(false)}
            onOk={() => setIsShowUploadModal(false)}
            closable={true}
            className="install-config-dialog-style"
            destroyOnClose={true}
            width={500}
            footer={[
              <Button key="cancel" onClick={() => setIsShowUploadModal(false)}>
                {lang(langIndex, "cancel") || "Cancel"}
              </Button>,
            ]}
          >
            <Upload
              beforeUpload={handleUpload}
              accept="*/*"
              showUploadList={false}
            >
              <Button
                type="primary"
                icon={<UploadOutlined />}
                loading={isUploading}
                disabled={isUploading}
                style={{ width: "100%" }}
              >
                {isUploading
                  ? lang(langIndex, "uploading") || "Uploading..."
                  : lang(langIndex, "upload_file") || "Upload File"}
              </Button>
            </Upload>
            <div style={{ marginTop: 10, fontSize: 12, color: "#999", textAlign: "center" }}>
              {lang(langIndex, "upload") || "Upload"} any file type
            </div>
          </Modal>
        </div>
      </Modal>
    </div>
  );
}

export default InstallConfigModal;

